import React, { useState } from "react";

import LinkIcon from "@uui/Link03";
import { filter, isEmpty, times } from "lodash/fp";

import { Button as MuiButton, Typography } from "@mui/material";

import SchedulingLinkDetail, {
  LoadingSchedulingLinkDetail,
} from "@app/analytics/Calendar/components/SchedulingLinkDetail";
import SchedulingLinkModal, {
  SchedulingLinkFormValues,
} from "@app/analytics/Calendar/components/SchedulingLinkModal";
import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import ROUTES from "@app/common/routes";
import type {
  PostCustomSchedulingLinkBody,
  PatchSchedulingLinkBody,
  SchedulingLink,
} from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { getSchedulingLinkUrl } from "@app/common/utils";
import { pluralizePhrase } from "@app/common/utils";
import Config from "@app/config";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import ConfirmationModal from "@rui/ConfirmationModal";
import DontShowThisAgain from "@rui/DontShowThisAgain";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

const CUSTOM_LINK_CREATE_DISABLED_TOOLTIP = `You've created the maximum of ${Config.rssLinks.maxCustom} custom scheduling links. Please delete a custom link to create more.`;

const LoadingState = () => (
  <Stack spacing={1.5}>
    {times(
      (i) => (
        <LoadingSchedulingLinkDetail key={i} />
      ),
      4
    )}
  </Stack>
);

const EmptyState = ({ onClickNew }: { onClickNew: () => void }) => (
  <EmptyStateContainer>
    <EmptyStateIcon Icon={LinkIcon} padding={2} />
    <EmptyStateTitle>No custom links</EmptyStateTitle>
    <EmptyStateSubtext>
      Create a new custom scheduling link to choose specific constraints like
      duration, video conferencing platform, and more.
    </EmptyStateSubtext>
    <Button onClick={onClickNew}>New custom link</Button>
  </EmptyStateContainer>
);

const CustomLinks = ({
  canCreateLink,
  isCreating,
  isLoading,
  isUpdating,
  links,
  onCreateLink,
  onDeleteLink,
  onUpdateLink,
}: {
  canCreateLink: boolean;
  isCreating: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  links: SchedulingLink[];
  onCreateLink: (
    link: PostCustomSchedulingLinkBody,
    { onSuccess }: { onSuccess: () => void }
  ) => void;
  onDeleteLink: (id: SchedulingLink["id"]) => void;
  onUpdateLink: (
    update: PatchSchedulingLinkBody,
    opts?: { onSuccess: () => void }
  ) => void;
}) => {
  const { connectCalendar } = useConnectCalendar();
  const { rssLinkSettings } = useRssLinksSettings();
  const [createLinkModalOpen, setCreateLinkModalOpen] = useState(false);
  const [linkToEdit, setLinkToEdit] = useState<SchedulingLink | undefined>();
  const [linkIdToDelete, setLinkIdToDelete] = useState<
    SchedulingLink["id"] | null
  >(null);
  const [linkIdToDisable, setLinkIdToDisable] = useState<
    SchedulingLink["id"] | null
  >(null);

  const [disableConfirmDismissed, setDisableConfirmDismissed] = useLocalStorage(
    "disableCustomLinkConfirmationDismissed",
    false,
    { migrateKeys: true }
  );

  const numActiveCustomLinks = filter("isEnabled", links).length;

  const handleConnectCalendar = (calendarProviderId: CalendarProviderId) =>
    connectCalendar(calendarProviderId, {
      returnTo: ROUTES.CALENDAR_SCHEDULING,
    });

  const updateLink = (
    update: PatchSchedulingLinkBody,
    opts?: { onSuccess: () => void }
  ) => onUpdateLink(update, opts);

  const handleToggleEnabled =
    (id: SchedulingLink["id"]) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked || disableConfirmDismissed) {
        updateLink({ id, isEnabled: checked });
      } else {
        setLinkIdToDisable(id);
      }
    };

  const handleUpdateLink = (
    body: SchedulingLinkFormValues,
    opts: { onSuccess: () => void }
  ) => {
    if (!linkToEdit) return;

    updateLink({ ...body, id: linkToEdit.id }, opts);
  };

  const handleConfirmDisable = () => {
    if (!linkIdToDisable) return;
    updateLink({ id: linkIdToDisable, isEnabled: false });
    setLinkIdToDisable(null);
  };

  const handleClickDelete = (id: SchedulingLink["id"]) => {
    setLinkIdToDelete(id);
  };

  const handleConfirmDelete = () => {
    if (!linkIdToDelete) return;
    onDeleteLink(linkIdToDelete);
    setLinkIdToDelete(null);
  };

  const handleToggleCreateModal = () => setCreateLinkModalOpen((open) => !open);
  const handleClickEdit = (link: SchedulingLink) => setLinkToEdit(link);
  const handleCancelEdit = () => setLinkToEdit(undefined);

  return (
    <>
      <Stack spacing={2}>
        <Row variant="apart" spacing={2} sx={{ minHeight: "30px" }}>
          <Typography variant="bodySecondaryReg">
            <Bold>{pluralizePhrase(links.length, "link")}</Bold>
            {isEmpty(links) ? "" : ` (${numActiveCustomLinks} active)`}
          </Typography>
          <TooltipWrapper
            show={!canCreateLink}
            title={CUSTOM_LINK_CREATE_DISABLED_TOOLTIP}
          >
            <MuiButton
              disabled={!canCreateLink}
              onClick={handleToggleCreateModal}
              sx={{ "&.Mui-disabled": { bgcolor: "shades.neutral.50" } }}
            >
              New custom link
            </MuiButton>
          </TooltipWrapper>
        </Row>
        {isLoading ? (
          <LoadingState />
        ) : isEmpty(links) ? (
          <EmptyState onClickNew={handleToggleCreateModal} />
        ) : (
          <Stack spacing={1.5}>
            {links.map((link) => (
              <SchedulingLinkDetail
                key={link.id}
                link={link}
                linkUrl={getSchedulingLinkUrl(
                  rssLinkSettings.vanityId,
                  link.vanityId
                )}
                onDelete={handleClickDelete}
                onEdit={handleClickEdit}
                onToggleEnabled={handleToggleEnabled(link.id)}
                workingHoursEnabled={rssLinkSettings.workingHoursEnabled}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <ConfirmationModal
        title="Disable custom link?"
        open={linkIdToDisable != null}
        onClose={() => setLinkIdToDisable(null)}
        onConfirm={handleConfirmDisable}
        size="md"
      >
        <Stack spacing={1.5}>
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Anyone with access to this custom link will no longer be able to
            schedule a meeting while it's disabled.
          </Typography>
          <DontShowThisAgain
            checked={disableConfirmDismissed}
            onChange={setDisableConfirmDismissed}
          />
        </Stack>
      </ConfirmationModal>
      <ConfirmationModal
        title="Delete custom link?"
        open={linkIdToDelete != null}
        onClose={() => setLinkIdToDelete(null)}
        onConfirm={handleConfirmDelete}
        size="md"
        confirmProps={{
          type: "negative",
          label: "Delete",
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Once deleted, this link can no longer be used for meeting
            scheduling.
          </Typography>
          <Alert
            severity="info"
            embedded
            alignItems="flex-start"
            sx={{ color: "shades.neutral.600" }}
          >
            If you would rather turn this link off temporarily, use the toggle
            to disable it.
          </Alert>
        </Stack>
      </ConfirmationModal>
      <SchedulingLinkModal
        isSaving={isCreating}
        onClose={handleToggleCreateModal}
        onConnectCalendar={handleConnectCalendar}
        open={createLinkModalOpen}
        platformOptions={rssLinkSettings.platformOptions}
        title="Create new custom link"
        onSave={onCreateLink}
      />
      <SchedulingLinkModal
        initialValues={linkToEdit}
        isSaving={isUpdating}
        onConnectCalendar={handleConnectCalendar}
        onClose={handleCancelEdit}
        open={linkToEdit != null}
        platformOptions={rssLinkSettings.platformOptions}
        title="Edit custom link"
        onSave={handleUpdateLink}
      />
    </>
  );
};

export default CustomLinks;
